import { createGlobalStyle, css } from 'styled-components';

import { themeColor, themeOpaqueColor, forDeviceType } from 'utils/mixins';

import { antdTokens } from './antd';

const disabledActivableInputStyle = css`
  color: rgba(0, 0, 0, 0.25);
  background-color: #f5f5f5;
  cursor: pointer;
  opacity: 1;
`;

export default createGlobalStyle`
  ::-moz-selection { /* Code for Firefox */
    color: inherit;
    background: ${themeOpaqueColor('lightBlue', 0.4)};
  }
  
  ::selection {
    color: inherit;
    background: ${themeOpaqueColor('lightBlue', 0.4)};
  }

  body,
  .ant-input {
    font-variant: tabular-nums;
    font-feature-settings: 'tnum';
  }

  body {
    color: ${antdTokens.colorText};
    background-color: ${antdTokens.colorBgContainer};
  }

  h1, h2, h3, h4, h5, h6 {
    color: ${antdTokens.colorTextHeading};
  }

  a[disabled] {
    color: ${antdTokens.colorTextDisabled};
    cursor: not-allowed;
    pointer-events: none;
  }

  caption {
    color: ${antdTokens.colorTextSecondary};
  }

  th {
    text-align: inherit;
  }

  mark {
    padding: 0.2em;
    background-color: #feffe6;
  }

  a {
    color: ${antdTokens.colorLink};
    text-decoration: none;
    background-color: transparent;
    outline: none;
    cursor: pointer;
    transition: color 0.3s;

    &:hover {
      color: ${antdTokens.colorLinkHover};
    }

    &:active {
      color: ${antdTokens.colorLinkActive};
    }

    &:active,
    &:hover,
    &:focus {
      text-decoration: none;
      outline: 0;
    }
  }

  body {
    margin: 0;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    line-height: ${({ theme }) => theme.lineHeights.sm};
  }

  html {
   -ms-overflow-style: -ms-autohiding-scrollbar;
  }

  h1, h2, h3, h4, b, strong, sub b, sub strong, details b, details strong {
    font-family: 'Proxima Nova Semibold', Corbel, Arial, sans-serif;
    font-weight: bold;
  }

  h1 {
    font-family: 'Proxima Nova', Corbel, Arial, sans-serif;
    font-size: ${({ theme }) => theme.fontSizes.h1};
    line-height: ${({ theme }) => theme.lineHeights.xxlg};
  }

  h2 {
    font-size: ${({ theme }) => theme.fontSizes.h2};
    line-height: ${({ theme }) => theme.lineHeights.lg};
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  h3 {
    font-size: ${({ theme }) => theme.fontSizes.h3};
    line-height: ${({ theme }) => theme.lineHeights.md};
    letter-spacing: 0.5px;
    text-transform: capitalize;

    ${forDeviceType(
      'mobile',
      `
      font-family: 'Proxima Nova', Corbel, Arial, sans-serif;
    `
    )}
  }

  sub, sub b, sub strong {
    font-size: ${({ theme }) => theme.fontSizes.sm};
    line-height: ${({ theme }) => theme.lineHeights.sm};
    letter-spacing: 0.2px;
    text-transform: uppercase;
  }

  p {
    font-size: ${({ theme }) => theme.fontSizes.md};
    line-height: ${({ theme }) => theme.lineHeights.sm};
  }

  b {
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    line-height: ${({ theme }) => theme.lineHeights.sm};
  }

  details, details b, details strong {
    font-size: ${({ theme }) => theme.fontSizes.xsm};
    line-height: ${({ theme }) => theme.lineHeights.xsm};
    letter-spacing: 0.2px;
    text-transform: uppercase;
  }

  .ant-message.ant-message {
    top: auto;
    bottom: 24px;
    height: auto;
    position: fixed;
    z-index: 999999999;
  }

  .ant-tooltip {
    z-index: 999999999;
  }

  .ant-tooltip,
  .ant-popover {
    filter: none;
  }

  .ant-popover .ant-popover-container {
    box-shadow:
      0 3px 6px -4px rgba(0, 0, 0, 0.12),
      0 6px 16px 0 rgba(0, 0, 0, 0.08),
      0 9px 28px 8px rgba(0, 0, 0, 0.05);
  }

  .ant-message .ant-message-notice.ant-message-notice {
    padding: 0px;
    border-radius: ${({ theme }) => theme.radius.lg};
    font-size: ${({ theme }) => theme.fontSizes.sm};
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
    & span {
    word-break: keep-all;
    } 
  }

  .ant-message .ant-message-success,
  .ant-message .ant-message-error {
    padding: 20px 26px;
    border-radius: ${({ theme }) => theme.radius.lg};
    color: white;
    max-width: 372px;
    display: flex;
    gap: 0;
    text-align: left;

    .ant-message-notice-title.ant-message-notice-title {
      color: inherit;
    }
  }

  .ant-message-error {
    background-color: ${themeColor('red')}
  }

  .ant-message-success {
    background-color: ${themeColor('green')}
  }

  .ant-select .ant-select-suffix {
    position: relative;
    inset-block-start: 1px;
    inset-inline-start: 1px;
  }

  .ant-select-dropdown .ant-select-item.ant-select-item-option-selected:not(.ant-select-item-option-disabled) {
    background-color: ${themeColor('darkBlue')};
    color: ${themeColor('white')};
  }

  .ant-checkbox-wrapper {
    display: inline-block;
    align-items: normal;

    &::after {
      display: none;
    }
  }

  .ant-checkbox-wrapper.ant-checkbox-wrapper {
    line-height: unset;
  }

  .ant-checkbox-wrapper .ant-checkbox {
    display: inline-block;
    vertical-align: middle;
    align-self: auto;
    top: -0.09em;
  }

  .ant-checkbox-wrapper .ant-checkbox:not(.ant-checkbox-indeterminate)::after {
    inset-inline-start: 22%;
  }

  .ant-picker .ant-picker-suffix {
    display: block;

    > .anticon {
      display: inline-block;
      vertical-align: -0.125em;

      > svg {
        vertical-align: baseline;
      }
    }
  }

  .ant-radio-wrapper:last-child {
    margin-inline-end: ${antdTokens.marginXS}px;
  }

  .ant-empty-normal,
  .ant-empty-small {
    color: ${antdTokens.colorTextDisabled};

    .ant-empty-description {
      color: inherit;
    }
  }

  .ant-spin.ant-spin:has(> .ant-spin-container) {
    color: inherit;
    font-size: inherit;
    line-height: inherit;
  }

  .ant-spin > .ant-spin-section {
    top: min(50%, 200px);
  }

  .ant-spin.ant-spin-section:not(.ant-spin-fullscreen) {
    display: inline-block;
    vertical-align: middle;
    text-align: center;
  }

  .ant-spin.ant-spin-section > .ant-spin-dot-holder {
    display: contents;
  }

  .ant-spin .ant-spin-description {
    line-height: ${antdTokens.lineHeight};
  }

  .ant-spin.ant-spin-lg {
    --ant-spin-dot-item-size: 14px;
  }

  .ant-table-column-sorter.ant-table-column-sorter,
  .ant-table-wrapper .ant-table-column-sorters:hover .ant-table-column-sorter {
    color: #bfbfbf;
  }

  .ant-table-wrapper .ant-table-sticky-scroll {
    display: none;
  }

  .ant-table-wrapper .ant-table-container::before,
  .ant-table-wrapper .ant-table-container::after {
    bottom: 0;
  }

  .ant-table-wrapper .ant-table-cell-fix-start-shadow-show::after,
  .ant-table-wrapper .ant-table-fix-start-shadow-show .ant-table-container::before {
    box-shadow: inset 10px 0 8px -8px rgba(0, 0, 0, 0.15);
  }

  .ant-table-wrapper .ant-table-cell-fix-end-shadow-show::after,
  .ant-table-wrapper .ant-table-fix-end-shadow-show .ant-table-container::after {
    box-shadow: inset -10px 0 8px -8px rgba(0, 0, 0, 0.15);
  }

  .ant-upload-wrapper {
    line-height: inherit;

    .ant-upload {
      line-height: ${antdTokens.lineHeight};
    }
  }

  .ant-switch:hover:not(.ant-switch-disabled) {
    background: ${antdTokens.colorTextQuaternary};
  }

  .ant-switch.ant-switch-checked:hover:not(.ant-switch-disabled) {
    background: ${antdTokens.colorPrimary};
  }

  .ant-switch.ant-switch-loading,
  .ant-switch.ant-switch-disabled {
    opacity: 0.4;
  }

  .ant-input-textarea-show-count .ant-input-data-count {
    bottom: ${-(antdTokens.fontSize * antdTokens.lineHeight + antdTokens.lineWidth)}px;
    inset-inline-end: ${-antdTokens.lineWidth}px;
  }

  .ant-btn-color-default.ant-btn-variant-text {
    --ant-btn-bg-color-active: rgba(0, 0, 0, 0.028);
  }

  .ant-btn.ant-btn-variant-solid {
    --ant-btn-border-color: var(--ant-btn-solid-bg-color);
    --ant-btn-border-color-hover: var(--ant-btn-solid-bg-color-hover);
    --ant-btn-border-color-active: var(--ant-btn-solid-bg-color-active);
  }

  .ant-btn-variant-solid {
    text-shadow: 0 -1px 0 rgba(0, 0, 0, 0.12);
  }

  .ant-btn-variant-solid:disabled,
  .ant-btn-variant-solid.ant-btn-disabled {
    text-shadow: none;
  }

  .ant-card .ant-card-head {
    min-height: 48px;

    .ant-card-head-title {
      padding: 16px 0;
    }
  }

  .ant-table-wrapper .ant-table-column-sorters {
    display: inline-flex;
    justify-content: flex-start;

    .ant-table-column-title {
      flex: 0 1 auto;
    }

    .ant-table-column-sorter {
      margin-inline-start: 8px;
    }
  }

  .ant-table-column-sorter-up.ant-table-column-sorter-up,
  .ant-table-column-sorter-down.ant-table-column-sorter-down {
    font-size: 11px;
  }

  .ant-pagination .ant-pagination-item,
  .ant-pagination .ant-pagination-jump-prev,
  .ant-pagination .ant-pagination-jump-next {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
      'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
      'Noto Color Emoji';
  }

  .ant-pagination .ant-pagination-prev,
  .ant-pagination .ant-pagination-next {
    font-family: Arial, Helvetica, sans-serif;
  }

  label.ant-checkbox-wrapper + label.ant-checkbox-wrapper {
    margin-left: 0;
  }

  div.ant-select-dropdown {
    padding: 0;
    filter: drop-shadow(0px 4px 8px ${themeOpaqueColor('darkBlack', 0.15)});
    border-radius: ${({ theme }) => theme.radius.sm};

    text-transform: uppercase;

    div.ant-select-item {
      padding: 12px;
      border-radius: 0;
    }
  }
  
  .ant-tooltip.ant-tooltip {
    --ant-tooltip-arrow-background-color: rgba(0, 0, 0, 0.75);
    --ant-tooltip-arrow-offset-horizontal: 15.5px;
  }

  .ant-popover.ant-popover {
    --ant-popover-arrow-offset-horizontal: 14px;
  }

  .ant-tooltip .ant-tooltip-container {
    background-color: ${themeColor('darkBlue')};
    border-radius: ${({ theme }) => theme.radius.sm};
    padding: ${({ theme }) => `${theme.spacings.sm} ${theme.spacings.md}`};
    box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
    line-height: 20px;
    min-width: 30px;
  }
  
  .centered-tooltip .ant-tooltip-container {
    text-align: center;
  }
  

  .orange-tooltip .ant-tooltip-container {
    background-color: ${themeColor('orange')};
  }

  .ant-tooltip.orange-tooltip {
    --ant-tooltip-arrow-background-color: ${themeColor('orange')};
  }

  .ant-btn {
    align-items: safe center;
    padding-block: 4px;
    line-height: ${antdTokens.lineHeight};
  }

  .ant-btn.ant-btn-lg {
    padding-block: 6.4px;
  }

  .ant-btn.ant-btn-sm {
    padding-block: 0;
  }

  a.ant-btn {
    line-height: 30px;

    &.ant-btn {
      padding-top: 0.01px;
    }

    &.ant-btn-lg {
      line-height: 38px;
    }

    &.ant-btn-sm {
      line-height: 22px;
    }
  }

  .ant-btn-icon-only.ant-btn-sm,
  .ant-btn-icon-only.ant-btn-lg {
    border-radius: 2px;
  }

  .ant-upload p {
    margin: 0;
  }

  .ant-progress.ant-progress-line .ant-progress-body {
    position: relative;
    top: 2px;
  }

  .ant-progress.ant-progress-line .ant-progress-indicator-end {
    flex: none;
    width: 2em;
    text-align: start;
  }

  .ant-btn > .ant-btn-icon:has(> .anticon) + span,
  .ant-btn > span + .ant-btn-icon:has(> .anticon),
  .ant-btn > .anticon + span,
  .ant-btn > span + .anticon {
    margin-inline-start: 8px;
  }

  .ant-btn:where(:has(> .ant-btn-icon > img)) {
    display: inline-block;

    > .ant-btn-icon {
      display: inline;
      line-height: inherit;
      font-size: inherit;
    }
  }

  .ant-btn > .anticon {
    display: inline-block;
    line-height: 1;

    svg {
      vertical-align: baseline;
    }
  }

  .ant-modal {
    padding-bottom: 24px;
  }

  .ant-alert.ant-alert-with-description {
    padding: 15px 15px 15px 24px;
    border-radius: 2px;

    .ant-alert-icon {
      margin-inline-end: 15px;
    }

    .ant-alert-title {
      margin-bottom: 4px;
    }
  }

  .ant-tabs {
    overflow: hidden;
  }

  .ant-modal-root .ant-modal-mask.ant-modal-mask {
    z-index: 999999;
    background-color: ${themeOpaqueColor('darkBlue', 0.7)};
  }

  div.ant-select-dropdown {
    z-index: 99999999;
  }

  .ant-modal-root .ant-modal-wrap.ant-modal-wrap {
    z-index: 99999999;
  }

  .onboarding-tutorial-mask {
    color: ${themeOpaqueColor('darkBlue', 0.4)};
  }

  .ant-card {
    min-height: fit-content;
  }

  div.ant-select-dropdown {
    text-transform: initial;
  }

  .mobile-only {
    display: none;
    ${forDeviceType('mobile', `display: block !important;`)}
  }

  .desktop-only {
    display: block;
    ${forDeviceType('mobile', `display: none !important;`)}
  }

  .activable-disabled-input {
    cursor: pointer;
  }

  .ant-input.activable-disabled-input {
    &&& {
      ${disabledActivableInputStyle}
    }
  }

  .ant-select.activable-disabled-input {
    &&& {
      ${disabledActivableInputStyle}

      & .ant-select-content,
      & .ant-select-selection-item {
        color: rgba(0, 0, 0, 0.25);
      }
    }
  }

  .ant-checkbox-wrapper.activable-disabled-input {
    &&& .ant-checkbox-checked {
      opacity: 0.6;
    }
  }

  .ant-table-filter-dropdown {
    &&& {
      border-radius: ${({ theme }) => theme.radius.lg};
    }
  }

  .ant-picker-dropdown .ant-picker-date-panel {
    width: 280px;

    .ant-picker-content {
      width: 252px;
    }
  }

  .ant-picker-dropdown .ant-picker-content th {
    height: 32px;
  }

  .ant-picker-dropdown .ant-picker-panel-container .ant-picker-panel.ant-picker-panel.ant-picker-panel {
    border-bottom: 1px solid ${antdTokens.colorSplit};

    &:has(+ .ant-picker-footer) {
      border-bottom-width: 0;
    }

    & + .ant-picker-footer {
      padding-bottom: 1px;
      border-bottom: 1px solid ${antdTokens.colorSplit};
    }
  }

  .ant-drawer-right > .ant-drawer-content-wrapper {
    box-shadow:
      -12px 0 48px 16px rgba(0, 0, 0, 0.03),
      -9px 0 28px 0 rgba(0, 0, 0, 0.05),
      -6px 0 16px -8px rgba(0, 0, 0, 0.08);
  }

  .ant-drawer .ant-drawer-title {
    line-height: 22px;
  }

  .ant-drawer .ant-drawer-body {
    line-height: ${antdTokens.lineHeight};
    overflow-wrap: break-word;
  }

  .ant-dropdown .ant-dropdown-menu {
    padding-inline: 0;
    text-align: left;
  }

  .ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-item,
  .ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-submenu-title {
    border-radius: 0;
    white-space: nowrap;
    line-height: 22px;
  }

  .ant-dropdown .ant-dropdown-menu .ant-dropdown-menu-title-content {
    display: contents;
  }
`;
